<script lang="ts" setup>
import { reactive, computed, watch, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useFeatureFlagsStore, useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { useDirtyFormGuard } from '@/composables/useDirtyFormGuard'
import type { FeatureFlagType } from '@/types/featureFlags'

const { t } = useI18n()

const props = defineProps<{
  flag: FeatureFlagType | null
  mode: 'create' | 'edit'
}>()

const emits = defineEmits<{
  (e: 'close'): void
  (e: 'saved'): void
}>()

const featureFlagsStore = useFeatureFlagsStore()
const notifyStore = useNotifyStore()
const { adminCategories } = storeToRefs(featureFlagsStore)

const data = reactive({
  loading: false,
  deleting: false,
  form: {
    category: '',
    name: '',
    enabled: false
  } as FeatureFlagType,
  originalCategory: '',
  originalName: '',
  errors: {} as Record<string, string>
})

// Track original data for dirty detection
const originalFormData = ref<FeatureFlagType | null>(null)
const isDirty = computed(() => {
  if (!originalFormData.value) return false
  return JSON.stringify(data.form) !== JSON.stringify(originalFormData.value)
})
useDirtyFormGuard('admin-feature-edit', isDirty)

// Reset form
const resetForm = () => {
  data.form = {
    category: '',
    name: '',
    enabled: false
  }
  data.originalCategory = ''
  data.originalName = ''
  data.errors = {}
  // Snapshot for dirty detection
  originalFormData.value = JSON.parse(JSON.stringify(data.form))
}

// Initialize form when flag changes
watch(
  () => props.flag,
  (newFlag) => {
    if (newFlag) {
      data.form = { ...newFlag }
      data.originalCategory = newFlag.category
      data.originalName = newFlag.name
      // Snapshot for dirty detection
      originalFormData.value = JSON.parse(JSON.stringify(data.form))
    } else {
      resetForm()
    }
    data.errors = {}
  },
  { immediate: true }
)

// Panel is visible when flag prop is truthy
const isVisible = computed(() => props.flag !== null)

const pageTitle = computed(() => {
  if (props.mode === 'create') return t('dashboard.admin.features.edit.newFlag')
  const flagName = `${data.form.category}.${data.form.name}`
  return flagName || t('dashboard.admin.features.edit.editFlag')
})

// Validation
const validate = (): boolean => {
  data.errors = {}
  let isValid = true

  if (!data.form.category.trim()) {
    data.errors.category = t('dashboard.admin.features.edit.validation.categoryRequired')
    isValid = false
  } else if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(data.form.category)) {
    data.errors.category = t('dashboard.admin.features.edit.validation.categoryFormat')
    isValid = false
  }

  if (!data.form.name.trim()) {
    data.errors.name = t('dashboard.admin.features.edit.validation.nameRequired')
    isValid = false
  } else if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(data.form.name)) {
    data.errors.name = t('dashboard.admin.features.edit.validation.nameFormat')
    isValid = false
  }

  return isValid
}

// Save
const handleSave = async (createAnother = false) => {
  if (!validate()) {
    notifyStore.createNotify({
      id: uuid(),
      title: t('dashboard.admin.features.edit.validation.validationError'),
      message: t('dashboard.admin.features.edit.validation.checkFields'),
      alertType: 'error'
    })
    return
  }

  data.loading = true

  // If editing and category/name changed, delete old and create new
  const categoryChanged = props.mode === 'edit' && data.originalCategory !== data.form.category
  const nameChanged = props.mode === 'edit' && data.originalName !== data.form.name

  if (categoryChanged || nameChanged) {
    // Delete old flag first
    await featureFlagsStore.deleteFlag(data.originalCategory, data.originalName)
  }

  const success = await featureFlagsStore.createOrUpdateFlag(data.form)

  if (success) {
    notifyStore.createNotify({
      id: uuid(),
      title: t('common.success'),
      message: props.mode === 'create' ? t('dashboard.admin.features.edit.notifications.created') : t('dashboard.admin.features.edit.notifications.updated'),
      alertType: 'success'
    })
    emits('saved')

    if (createAnother) {
      resetForm()
    } else {
      emits('close')
    }
  }

  data.loading = false
}

// Delete
const handleDelete = async () => {
  if (!confirm(t('dashboard.admin.features.edit.confirmDelete', { category: data.form.category, name: data.form.name }))) {
    return
  }

  data.deleting = true

  const success = await featureFlagsStore.deleteFlag(data.form.category, data.form.name)

  if (success) {
    notifyStore.createNotify({
      id: uuid(),
      title: t('common.delete'),
      message: t('dashboard.admin.features.edit.notifications.deleted', { category: data.form.category, name: data.form.name }),
      alertType: 'success'
    })
    emits('saved')
    emits('close')
  }

  data.deleting = false
}

const handleClose = () => {
  emits('close')
}
</script>

<template>
  <Teleport to="body">
    <Transition name="slide">
      <div v-if="isVisible" class="edit-overlay" @click.self="handleClose">
        <div class="edit-panel">
          <!-- Header -->
          <header class="panel-header">
            <div class="header-left">
              <button class="back-btn" @click="handleClose">
                <v-icon icon="mdi-close" size="20" />
              </button>
              <div class="header-icon">
                <v-icon :icon="mode === 'create' ? 'mdi-flag-plus' : 'mdi-flag-variant'" size="24" />
              </div>
              <div class="header-text">
                <h1 class="panel-title">{{ pageTitle }}</h1>
                <p class="panel-subtitle">
                  {{ mode === 'create' ? $t('dashboard.admin.features.edit.createFlag') : $t('dashboard.admin.features.edit.editFlagData') }}
                </p>
              </div>
            </div>

            <div class="header-actions">
              <button
                v-if="mode === 'create'"
                class="save-another-btn"
                :disabled="data.loading"
                @click="handleSave(true)"
              >
                <v-icon icon="mdi-plus" size="18" />
                <span>{{ $t('dashboard.admin.features.edit.saveAndNew') }}</span>
              </button>
              <button
                class="save-btn"
                :disabled="data.loading"
                @click="handleSave(false)"
              >
                <v-progress-circular v-if="data.loading" indeterminate size="18" width="2" />
                <v-icon v-else icon="mdi-content-save" size="18" />
                <span>{{ $t('dashboard.admin.features.edit.save') }}</span>
              </button>
            </div>
          </header>

          <!-- Content -->
          <div class="panel-content">
            <!-- Flag Details -->
            <div class="form-section">
              <h3 class="section-title">{{ $t('dashboard.admin.features.edit.flagDetails') }}</h3>

              <div class="form-group" :class="{ error: data.errors.category }">
                <label class="form-label">{{ $t('dashboard.admin.features.edit.category') }}</label>
                <input
                  v-model="data.form.category"
                  type="text"
                  class="form-input"
                  :placeholder="$t('dashboard.admin.features.edit.categoryPlaceholder')"
                  list="category-list"
                />
                <datalist id="category-list">
                  <option v-for="cat in adminCategories" :key="cat" :value="cat" />
                </datalist>
                <span v-if="data.errors.category" class="error-text">{{ data.errors.category }}</span>
                <span class="hint-text">{{ $t('dashboard.admin.features.edit.categoryHint') }}</span>
              </div>

              <div class="form-group" :class="{ error: data.errors.name }">
                <label class="form-label">{{ $t('dashboard.admin.features.edit.name') }}</label>
                <input
                  v-model="data.form.name"
                  type="text"
                  class="form-input"
                  :placeholder="$t('dashboard.admin.features.edit.namePlaceholder')"
                />
                <span v-if="data.errors.name" class="error-text">{{ data.errors.name }}</span>
                <span class="hint-text">{{ $t('dashboard.admin.features.edit.nameHint') }}</span>
              </div>
            </div>

            <!-- Status -->
            <div class="form-section">
              <h3 class="section-title">{{ $t('dashboard.admin.features.edit.status') }}</h3>

              <div class="toggle-row">
                <label class="toggle-item">
                  <input v-model="data.form.enabled" type="checkbox" class="toggle-input" />
                  <span class="toggle-switch"></span>
                  <div class="toggle-content">
                    <span class="toggle-label">{{ data.form.enabled ? $t('dashboard.admin.features.edit.enabled') : $t('dashboard.admin.features.edit.disabled') }}</span>
                    <span class="toggle-hint">
                      {{ data.form.enabled ? $t('dashboard.admin.features.edit.enabledHint') : $t('dashboard.admin.features.edit.disabledHint') }}
                    </span>
                  </div>
                </label>
              </div>
            </div>

            <!-- Preview -->
            <div class="form-section preview-section">
              <h3 class="section-title">{{ $t('dashboard.admin.features.edit.preview') }}</h3>
              <div class="preview-box">
                <div class="preview-key">
                  <span class="preview-category">{{ data.form.category || $t('dashboard.admin.features.edit.previewCategory') }}</span>
                  <span class="preview-separator">.</span>
                  <span class="preview-name">{{ data.form.name || $t('dashboard.admin.features.edit.previewName') }}</span>
                </div>
                <div class="preview-value" :class="{ enabled: data.form.enabled }">
                  {{ data.form.enabled ? 'true' : 'false' }}
                </div>
              </div>
            </div>

            <!-- Delete Button (edit mode only) -->
            <div v-if="mode === 'edit'" class="form-section danger-section">
              <h3 class="section-title danger-title">{{ $t('dashboard.admin.features.edit.dangerZone') }}</h3>
              <button
                class="delete-btn"
                :disabled="data.deleting"
                @click="handleDelete"
              >
                <v-progress-circular v-if="data.deleting" indeterminate size="18" width="2" />
                <v-icon v-else icon="mdi-delete-outline" size="18" />
                <span>{{ $t('dashboard.admin.features.edit.deleteFlag') }}</span>
              </button>
              <span class="danger-hint">{{ $t('dashboard.admin.features.edit.deleteHint') }}</span>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.edit-overlay {
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 100;
  display: flex;
  justify-content: flex-end;
}

.edit-panel {
  width: 100%;
  max-width: 640px;
  height: 100%;
  background: rgba(15, 23, 48, 0.98);
  backdrop-filter: blur(20px);
  border-left: 1px solid rgba(120, 163, 255, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: rgba(156, 39, 176, 0.02);
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.9);
  }
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.15), rgba(186, 104, 200, 0.1));
  border: 1px solid rgba(156, 39, 176, 0.25);
  border-radius: 12px;

  .v-icon {
    color: #ba68c8;
  }
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.panel-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  white-space: nowrap;
}

.panel-subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
  white-space: nowrap;
}

.header-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.save-btn,
.save-another-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.save-btn {
  background: rgba(76, 175, 80, 0.15);
  border: 1px solid rgba(76, 175, 80, 0.3);
  color: #4caf50;

  &:hover:not(:disabled) {
    background: rgba(76, 175, 80, 0.2);
    border-color: rgba(76, 175, 80, 0.4);
  }
}

.save-another-btn {
  background: rgba(156, 39, 176, 0.1);
  border: 1px solid rgba(156, 39, 176, 0.25);
  color: #ba68c8;

  &:hover:not(:disabled) {
    background: rgba(156, 39, 176, 0.15);
    border-color: rgba(156, 39, 176, 0.35);
  }
}

/* Content */
.panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-section {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 12px;
  padding: 20px;
}

.section-title {
  margin: 0 0 16px 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.8);
}

/* Form */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;

  & + .form-group {
    margin-top: 16px;
  }

  &.error {
    .form-input {
      border-color: rgba(244, 67, 54, 0.5);
    }
  }
}

.form-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.7);
}

.form-input {
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: rgba(156, 39, 176, 0.4);
    background: rgba(255, 255, 255, 0.05);
  }
}

.error-text {
  font-size: 0.75rem;
  color: #f44336;
}

.hint-text {
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.4);
}

/* Toggle */
.toggle-row {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.toggle-item {
  display: flex;
  align-items: center;
  gap: 14px;
  cursor: pointer;
}

.toggle-input {
  display: none;
}

.toggle-switch {
  position: relative;
  width: 52px;
  height: 28px;
  background: rgba(244, 67, 54, 0.2);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 14px;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 20px;
    height: 20px;
    background: #f44336;
    border-radius: 50%;
    transition: all 0.2s ease;
  }
}

.toggle-input:checked + .toggle-switch {
  background: rgba(76, 175, 80, 0.2);
  border-color: rgba(76, 175, 80, 0.4);

  &::after {
    left: 27px;
    background: #4caf50;
  }
}

.toggle-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.toggle-label {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.toggle-hint {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

/* Preview */
.preview-section {
  background: rgba(156, 39, 176, 0.03);
  border-color: rgba(156, 39, 176, 0.1);
}

.preview-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
}

.preview-key {
  display: flex;
  align-items: center;
  gap: 0;
}

.preview-category {
  color: #ba68c8;
}

.preview-separator {
  color: rgba(200, 218, 255, 0.4);
}

.preview-name {
  color: #64b5f6;
}

.preview-value {
  padding: 4px 10px;
  background: rgba(244, 67, 54, 0.15);
  border: 1px solid rgba(244, 67, 54, 0.25);
  border-radius: 6px;
  color: #e57373;
  font-size: 0.85rem;

  &.enabled {
    background: rgba(76, 175, 80, 0.15);
    border-color: rgba(76, 175, 80, 0.25);
    color: #81c784;
  }
}

/* Danger Zone */
.danger-section {
  background: rgba(244, 67, 54, 0.03);
  border-color: rgba(244, 67, 54, 0.15);
}

.danger-title {
  color: #e57373;
}

.delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.25);
  border-radius: 10px;
  color: #f44336;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(244, 67, 54, 0.15);
    border-color: rgba(244, 67, 54, 0.35);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.danger-hint {
  display: block;
  margin-top: 10px;
  font-size: 0.75rem;
  color: rgba(244, 67, 54, 0.6);
  text-align: center;
}

/* Transitions */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;

  .edit-panel {
    transform: translateX(100%);
  }
}

.slide-enter-to,
.slide-leave-from {
  opacity: 1;

  .edit-panel {
    transform: translateX(0);
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .edit-panel {
    background: rgba(255, 255, 255, 0.98);
  }

  .panel-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .panel-subtitle {
    color: rgba(15, 23, 42, 0.5);
  }

  .form-section {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(28, 110, 227, 0.08);
  }

  .form-input {
    background: rgba(0, 0, 0, 0.02);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(15, 23, 42, 0.9);
  }
}

@media (max-width: 768px) {
  .edit-overlay {
    top: 56px;
  }

  .edit-panel {
    max-width: 100%;
  }

  .panel-header {
    flex-wrap: wrap;
    gap: 12px;
  }

  .header-actions {
    width: 100%;
    justify-content: stretch;
  }

  .save-btn,
  .save-another-btn {
    flex: 1;
    justify-content: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .slide-enter-active,
  .slide-leave-active {
    transition: none;
  }

  .back-btn,
  .save-btn,
  .toggle-switch,
  .toggle-switch::after,
  .form-input,
  .delete-btn {
    transition: none;
  }
}
</style>
